import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { formatDistanceToNow, format, parseISO, isValid } from 'date-fns';
import { reportsService } from '../../services/reports.service';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PageHeader } from '../../components/common/PageHeader';
import type { TrackingGapRow } from '../../types';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

function parseEventTime(value?: string): Date | null {
  if (!value) return null;
  // MySQL DATETIME often comes as "YYYY-MM-DD HH:mm:ss"
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const d = parseISO(normalized);
  return isValid(d) ? d : null;
}

function formatEventTime(value?: string, pattern = 'MMM d, HH:mm'): string {
  const d = parseEventTime(value);
  return d ? format(d, pattern) : '—';
}

function formatEventRelative(value?: string): string {
  const d = parseEventTime(value);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '';
}

export function TrackingAlertsPage() {
  const [selectedAlert, setSelectedAlert] = useState<TrackingGapRow | null>(null);

  const queryClient = useQueryClient();

  const {
    data: alerts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tracking-alerts'],
    queryFn: () => reportsService.getActiveAlerts(),
    refetchInterval: 60000,
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => reportsService.resolveAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracking-alerts'] });
      toast.success('Alert marked as resolved');
      setSelectedAlert(null);
    },
    onError: () => toast.error('Failed to resolve alert'),
  });

  if (isLoading) return <LoadingSpinner message="Loading tracking alerts..." />;
  if (error) {
    const apiMessage =
      (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      ?? (error as Error)?.message;
    return (
      <ErrorMessage
        title="Could not load tracking alerts"
        message={apiMessage || 'Failed to load tracking alerts. Please try again.'}
        onRetry={() => refetch()}
      />
    );
  }

  const activeAlerts = (alerts ?? []).filter((a) => !a.isResolved);
  const resolvedAlerts = (alerts ?? []).filter((a) => a.isResolved);

  return (
    <Box>
      <PageHeader
        compact
        title="Tracking Alerts"
        subtitle={`${activeAlerts.length} active alerts · ${resolvedAlerts.length} resolved`}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} aria-label="Refresh alerts">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {activeAlerts.length === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          All employees are being tracked normally. No active interruptions.
        </Alert>
      )}

      <Grid container spacing={2.5}>
        {/* Alert List */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Active Alerts ({activeAlerts.length})
              </Typography>

              {activeAlerts.length === 0 ? (
                <Box py={4} textAlign="center">
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No active tracking interruptions
                  </Typography>
                </Box>
              ) : (
                activeAlerts.map((alert) => (
                  <Box
                    key={alert.event.id}
                    onClick={() => setSelectedAlert(alert)}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selectedAlert?.event.id === alert.event.id ? 'error.main' : 'divider',
                      bgcolor: selectedAlert?.event.id === alert.event.id
                        ? (t) => t.palette.error.main + '14'
                        : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'error.light',
                        bgcolor: (t) => t.palette.error.main + '0A',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {alert.employee?.name ?? 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {alert.employee?.employeeCode}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={`${alert.gapMinutes} min`}
                        size="small"
                        color="error"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block">
                      Started: {formatEventTime(alert.event.startTime)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatEventRelative(alert.event.startTime)}
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      fullWidth
                      sx={{ mt: 1.5 }}
                      startIcon={<CheckCircleIcon />}
                      disabled={resolveMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        resolveMutation.mutate(alert.event.id);
                      }}
                    >
                      Mark Resolved
                    </Button>
                  </Box>
                ))
              )}

              {resolvedAlerts.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" mt={3} mb={1.5}>
                    Resolved Today ({resolvedAlerts.length})
                  </Typography>
                  {resolvedAlerts.map((alert) => (
                    <Box
                      key={alert.event.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        opacity: 0.7,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {alert.employee?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Gap: {alert.gapMinutes} min
                          </Typography>
                        </Box>
                        <Chip label="Resolved" size="small" color="default" variant="outlined" />
                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Map showing last known location */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', minHeight: 500 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, height: '100%' }}>
              {selectedAlert ? (
                <Box height="100%">
                  <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Last Known Location: {selectedAlert.employee?.name}
                    </Typography>
                    {selectedAlert.event.lastKnownLatitude && (
                      <Typography variant="caption" color="text.secondary">
                        <LocationOnIcon sx={{ fontSize: 12 }} />{' '}
                        {selectedAlert.event.lastKnownLatitude.toFixed(4)},{' '}
                        {selectedAlert.event.lastKnownLongitude?.toFixed(4)}
                      </Typography>
                    )}
                  </Box>
                  {selectedAlert.event.lastKnownLatitude ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <Map
                        mapId="alert-map"
                        center={{
                          lat: selectedAlert.event.lastKnownLatitude,
                          lng: selectedAlert.event.lastKnownLongitude ?? 0,
                        }}
                        zoom={15}
                        gestureHandling="greedy"
                        style={{ width: '100%', height: 'calc(100% - 64px)' }}
                      >
                        <AdvancedMarker
                          position={{
                            lat: selectedAlert.event.lastKnownLatitude,
                            lng: selectedAlert.event.lastKnownLongitude ?? 0,
                          }}
                        />
                      </Map>
                    </APIProvider>
                  ) : (
                    <Box
                      height="calc(100% - 64px)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                      gap={1}
                    >
                      <LocationOnIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        No location data available for this employee
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={2}
                  sx={{ minHeight: 500 }}
                >
                  <WarningAmberIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                  <Typography variant="body1" color="text.secondary">
                    Select an alert to view last known location
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
